import { ReactNode, useEffect } from "react";
import { faEnvelope, faMessagePen, faPhone } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { toDateTime } from "common/utils/dates";
import { parseNumber } from "common/utils/numbers";
import { OrderPriceSummary } from "common/types/priceSummary";
import { ReviewRating } from "common/types";
import { toPhone } from "common/utils/formatting";
import { useMatch, useSearchState } from "common/router/hooks";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";
import { OrderStatus as OrderStatusEnum } from "common/types/orders";

import Body from "client/common/components/layouts/Body";
import Subheader from "client/common/components/layouts/Subheader";
import Page from "client/common/components/layouts/Page";
import PaymentMethod from "client/common/components/content/PaymentMethod";
import Address from "client/common/components/content/Address";
import LoadingPage from "client/common/components/loading/LoadingPage";
import Error404Page from "client/common/components/errors/Error404Page";
import { ModalStatus } from "client/common/types";
import ReviewCreateModal from "client/reviews/components/ReviewCreateModal";
import Box from "client/common/components/layouts/Box";
import PillButton from "client/common/components/buttons/PillButton";
import Button from "client/common/components/buttons/Button";
import { useClientQuery } from "client/accounts/hooks";
import { useCartItemAddMutation } from "client/carts/hooks";
import { CartCode } from "client/carts/types";
import PriceSummary from "client/common/components/display/PriceSummary";
import { useBreakpoints } from "client/common/hooks";
import { useGenerateOrderPdfMutation } from "client/pdfs/hooks";
import { Paths, urls } from "client/routes";
import Banner from "client/common/components/content/Banner";
import Tooltip from "client/common/components/content/Tooltip";
import Alert from "client/common/components/content/Alert";
import EmployeePricingNote from "client/common/components/content/EmployeePricingNote";
import { sendAddToCart, useSendPostHogAddToCart } from "client/analytics/utils";
import { AddToCartPageSource } from "client/analytics/types";

import { useOrderQuery } from "../hooks";
import OrderTrackingModal from "./OrderTrackingModal";
import { IOrder, IOrderItem, OrderSearch } from "../types";
import OrderStatus from "./OrderStatus";
import OrderItem from "./OrderItem";
import OrderModificationsModal from "./OrderModificationsModal";
import { useOrderCancelationDrawer, useOrderPaymentUpdateDrawer } from "../stackables";

import styles from "../styles/OrderPage.module.less";

type SummaryItem = {
  label: string;
  value: ReactNode;
  className: string;
  action?: ReactNode;
};

type OrderItemDetailProps = {
  order: IOrder;
  orderItem: IOrderItem;
  onSearchChange: (search: Partial<OrderSearch>) => void;
  onOpenModifications: () => void;
};

const OrderItemDetail = (props: OrderItemDetailProps) => {
  const { order, orderItem, onSearchChange, onOpenModifications } = props;
  const sendPostHogAddToCart = useSendPostHogAddToCart();

  // We may want to add a toast on error here.
  const { mutate: addCartItem, isPending: isAdding } = useCartItemAddMutation({
    shouldOpenCart: true,
    onSuccess: (cartItemId, quantity, body) => {
      sendAddToCart({
        item: orderItem.item,
        quantity,
        price: orderItem.unit_price,
        discount: 0,
      });
      sendPostHogAddToCart({
        source: AddToCartPageSource.OrderItem,
        cart_id: typeof body.cart_id === "number" ? body.cart_id : null,
        cart_item_id: cartItemId,
        item_id: body.item,
        product_id: orderItem.item.product.id,
        product_line_id: orderItem.item.product.product_line.id,
        patient_id: body.patient_id ?? null,
        flavor_selection_id: body.flavor_selection ?? null,
        quantity,
        unit_price: orderItem.unit_price,
        discount: null,
      });
    },
  });

  return (
    <Box className={styles.orderItem} key={orderItem.item.id} outline={false}>
      <OrderItem
        order={order}
        orderItem={orderItem}
        onOpenReview={(rating) =>
          onSearchChange({
            reviewCreateModalIsVisible: ModalStatus.Open,
            productLineId: orderItem.item.product.product_line.id,
            initialRating: rating,
          })
        }
        onOpenModifications={onOpenModifications}
        classNames={{
          container: styles.container,
          info: styles.info,
          item: styles.item,
          content: styles.content,
          image: styles.image,
          timeline: styles.timeline,
        }}
      />
      <div className={styles.actions}>
        <div className={styles.group}>
          {orderItem.tracker && (
            <Button
              size="s"
              variant="secondary"
              className={styles.action}
              onClick={() => onSearchChange({ trackingModalIsVisible: ModalStatus.Open })}
            >
              View tracking information
            </Button>
          )}
          <Button
            size="s"
            isLoading={isAdding}
            className={styles.action}
            onClick={() =>
              addCartItem({
                item: orderItem.item.id,
                cart_id: CartCode.ACTIVE,
                flavor_selection: orderItem.flavor_selection?.id,
                quantity: 1,
                patient_id: orderItem.patient?.id,
              })
            }
          >
            Reorder item
          </Button>
        </div>
      </div>
    </Box>
  );
};
const initialTrackingModalSearch = {
  trackingModalIsVisible: ModalStatus.Closed,
  trackerId: undefined,
};

const initialReviewCreateModalSearch = {
  reviewCreateModalIsVisible: ModalStatus.Closed,
  initialRating: undefined,
  productLineId: undefined,
};

const initialPaymentModalSearch = {
  paymentModalIsVisible: ModalStatus.Closed,
};

const initialModificationsModalSearch = {
  modificationsModalIsVisible: ModalStatus.Closed,
};

const initialCancelationModalSearch = {
  cancelationModalIsVisible: ModalStatus.Closed,
};

const OrderPage = () => {
  const { open: openCancelationDrawer } = useOrderCancelationDrawer();
  const { open: openPaymentUpdateDrawer } = useOrderPaymentUpdateDrawer();
  const match = useMatch();
  const id = parseNumber(match.params.id, 0);
  const { data: client } = useClientQuery();
  const { isPhoneAndSmaller } = useBreakpoints();

  const { data: order, isLoading: isOrderLoading } = useOrderQuery(parseNumber(id, 0));
  const [params, setParams] = useSearchState<Paths["orders"]["detail"]>({
    ...initialTrackingModalSearch,
    ...initialReviewCreateModalSearch,
    ...initialPaymentModalSearch,
    ...initialModificationsModalSearch,
    ...initialCancelationModalSearch,
  });

  const { mutate: printOrderPdf, isLoading: isPrintPdfLoading } = useGenerateOrderPdfMutation();
  const { mutate: viewOrderPdf, isLoading: isViewPdfLoading } = useGenerateOrderPdfMutation(false);

  // we should eventually move all modals to hooks like this.
  // see the subscription detail page for a full example
  useEffect(() => {
    if (params.cancelationModalIsVisible === ModalStatus.Open) {
      openCancelationDrawer({
        orderId: id,
        onClose: () => setParams({ ...params, ...initialCancelationModalSearch }),
      });
    }
    // we only want to open the drawer once, when the params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.cancelationModalIsVisible]);

  useEffect(() => {
    if (params.paymentModalIsVisible === ModalStatus.Open) {
      openPaymentUpdateDrawer({
        id,
        onClose: () => setParams({ ...params, ...initialPaymentModalSearch }),
      });
    }
    // we only want to open the drawer once, when the params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.paymentModalIsVisible]);

  if (isOrderLoading) {
    return <LoadingPage />;
  }

  if (!order) {
    return (
      <Error404Page
        title="Order Not Found!"
        description="We could not find that order."
        buttonText="Back to Orders"
        buttonLink={urls.orders.list.to()}
      />
    );
  }

  const summary: SummaryItem[] = [
    ...(!order.shipping_address && order.has_any_pickup
      ? [
          {
            label: "Pickup Address",
            value: order.pickup_address ? <Address address={order.pickup_address} /> : "-",
            className: styles.block,
          },
        ]
      : [
          {
            label: "Shipping Address",
            value: order.shipping_address ? <Address address={order.shipping_address} /> : "-",
            className: styles.block,
          },
        ]),
    {
      label: "Shipping Updates",
      value: client ? (
        <div className={styles.content}>
          {[
            {
              icon: faEnvelope,
              value: client.primary_contact_email?.email,
            },
            {
              icon: faPhone,
              value: toPhone(client.primary_contact_number?.number),
            },
            {
              icon: faMessagePen,
              value: order.client_note,
            },
          ]
            .filter(({ value }) => !!value)
            .map(({ icon, value }) => (
              <div key={value} className={styles.subcontent}>
                <Icon fixedWidth={true} icon={icon} />
                <div className={styles.subvalue}>{value}</div>
              </div>
            ))}
        </div>
      ) : (
        <Spinner />
      ),
      action: (
        <PillButton
          dest={urls.accounts.detail({
            search: { form: "updateAccount", visible: ModalStatus.Open },
          })}
          variant="secondary"
          size="xs"
        >
          Edit Contact Info
        </PillButton>
      ),
      className: styles.block,
    },
    {
      label: "Paid with",
      value: <PaymentMethod method={order.payment_method} />,
      action: order.can_update_payment_method && (
        <PillButton
          variant="secondary"
          onClick={() => setParams({ paymentModalIsVisible: ModalStatus.Open })}
          size="xs"
        >
          Edit Payment Info
        </PillButton>
      ),
      className: styles.block,
    },
    {
      label: "Summary",
      value: (
        <div className={styles.priceSummarySection}>
          <PriceSummary<OrderPriceSummary>
            data={order}
            styles={{
              rows: styles.rows,
              row: styles.row,
              subrows: styles.subrows,
              total: styles.total,
            }}
          />
          {!!order.employee_savings && <EmployeePricingNote savings={order.employee_savings} />}
          {!!order.refund_description && (
            <Alert variant="warning">{order.refund_description}</Alert>
          )}
        </div>
      ),
      className: styles.totals,
    },
  ];

  const actions = [
    {
      label: "Print invoice",
      isLoading: isPrintPdfLoading,
      onClick: () => printOrderPdf({ order: order.id }),
    },
    {
      label: "View invoice",
      isLoading: isViewPdfLoading,
      onClick: () => viewOrderPdf({ order: order.id }),
    },
    ...(order.status !== OrderStatusEnum.Canceled
      ? [
          {
            label: "Cancel order",
            isLoading: false,
            onClick: () => setParams({ cancelationModalIsVisible: ModalStatus.Open }),
            disabled: !order.is_cancelable,
            tooltipText: order.is_cancelable
              ? "Your cancelation window is limited. Please cancel now, if needed."
              : "We're sorry — this order is already processing and cannot be canceled.",
          },
        ]
      : []),
  ];

  const getModificationLabel = () => {
    if (order.shipping_address_modified && order.payment_method_modified) {
      return "Shipping address and payment method modified since order placement";
    }
    if (order.shipping_address_modified) {
      return "Shipping address modified since order placement";
    }
    if (order.payment_method_modified) {
      return "Payment method modified since order placement";
    }
    return null;
  };
  const modificationLabel = getModificationLabel();
  const openModificationsModal = () =>
    setParams({ ...params, modificationsModalIsVisible: ModalStatus.Open });

  return (
    <Page>
      <Subheader
        title={`Order ${order.display_number}`}
        description={<div>Order placed on {toDateTime(order.order_placed_time)}</div>}
        renderStatus={() => <OrderStatus order={order} />}
        renderActions={() =>
          // TODO sc25305 - replace with proper order printing
          !isPhoneAndSmaller &&
          actions.map((action) => (
            <Tooltip
              key={action.label}
              isEnabled={!!action.tooltipText}
              content={action.tooltipText ?? ""}
              position="below"
              align="right"
              maxWidth={400}
            >
              <PillButton
                isLoading={action.isLoading}
                variant="secondary"
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.label}
              </PillButton>
            </Tooltip>
          ))
        }
      />
      <Body>
        <div className={styles.main}>
          {order.order_items.map((orderItem) => (
            <OrderItemDetail
              key={orderItem.id}
              order={order}
              orderItem={orderItem}
              onSearchChange={setParams}
              onOpenModifications={openModificationsModal}
            />
          ))}
          <div className={styles.summary}>
            {modificationLabel && (
              <Banner.LinkButton
                variant="primary"
                className={styles.banner}
                onClick={openModificationsModal}
              >
                <Banner.Text label={modificationLabel} />
              </Banner.LinkButton>
            )}
            {summary.map((info) => (
              <div className={cx(styles.info, info.className)} key={info.label}>
                <div className={styles.primary}>
                  <div className={styles.label}>{info.label}</div>
                  <div className={styles.value}>{info.value}</div>
                </div>
                {order.can_update && <div className={styles.action}>{info.action}</div>}
              </div>
            ))}
          </div>
          {isPhoneAndSmaller && (
            <div className={styles.mobileActions}>
              {actions.map((action) => (
                <div key={action.label} className={styles.mobileAction}>
                  <Tooltip
                    isEnabled={!!action.tooltipText}
                    content={action.tooltipText ?? ""}
                    align="right"
                  >
                    <Button
                      isLoading={action.isLoading}
                      variant="secondary"
                      onClick={action.onClick}
                      disabled={action.disabled}
                    >
                      {action.label}
                    </Button>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
        </div>
      </Body>
      <OrderTrackingModal
        trackers={order.trackers.filter((tracker) =>
          params.trackerId ? tracker.id === parseInt(`${params.trackerId}`, 10) : true,
        )}
        shippingAddress={order.shipping_address}
        visible={params.trackingModalIsVisible === ModalStatus.Open}
        onClose={() => setParams({ ...params, ...initialTrackingModalSearch })}
      />
      <ReviewCreateModal
        visible={params.reviewCreateModalIsVisible === ModalStatus.Open}
        onClose={() => setParams({ ...params, ...initialReviewCreateModalSearch })}
        initialRating={parseNumber(params.initialRating) as ReviewRating}
        productLineId={parseNumber(params.productLineId)}
      />
      <OrderModificationsModal
        visible={params.modificationsModalIsVisible === ModalStatus.Open}
        onClose={() => setParams({ ...params, ...initialModificationsModalSearch })}
        orderId={order.id}
      />
    </Page>
  );
};

export default OrderPage;
